import React, { useState } from 'react';
import axios from 'axios';
import { Thermometer, Send, CheckCircle, AlertTriangle, XCircle, Activity } from 'lucide-react';
import { Button } from '../components/ui/button';
import DataSourceBadge from '../components/common/DataSourceBadge';
import { toast } from 'sonner';
import MonitoringSessionManager from '../components/MonitoringSessionManager';
import permissions from '../lib/permissions';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SensorTestConsole = ({ user }) => {
  const perms = permissions(user?.role);
  const [formData, setFormData] = useState({
    batch_id: '',
    product_name: '',
    product_type: '',
    species_or_category: '',
    test_session_id: '',
    temperature_celsius: '',
  });

  const [response, setResponse] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.batch_id || !formData.temperature_celsius) {
      toast.error('Batch ID and Temperature are required');
      return;
    }

    setLoading(true);
    setResponse(null);

    try {
      // Prepare payload - only include non-empty fields
      const payload = {
        batch_id: formData.batch_id,
        temperature_celsius: parseFloat(formData.temperature_celsius),
      };

      if (formData.product_name) payload.product_name = formData.product_name;
      if (formData.product_type) payload.product_type = formData.product_type;
      if (formData.species_or_category) payload.species_or_category = formData.species_or_category;
      if (formData.test_session_id) payload.test_session_id = formData.test_session_id;

      const res = await axios.post(`${BACKEND_URL}/api/sensors/temperature`, payload);
      
      setResponse(res.data);
      toast.success('Temperature reading recorded successfully!');
      
      // Clear form
      setFormData({
        batch_id: '',
        product_name: '',
        product_type: '',
        species_or_category: '',
        test_session_id: '',
        temperature_celsius: '',
      });
    } catch (error) {
      console.error('Error submitting reading:', error);
      toast.error('Failed to record temperature reading');
      setResponse({ error: error.response?.data?.detail || error.message });
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'normal':
        return <CheckCircle className="w-6 h-6 text-emerald-600" />;
      case 'warning':
        return <AlertTriangle className="w-6 h-6 text-amber-600" />;
      case 'critical':
        return <XCircle className="w-6 h-6 text-rose-600" />;
      default:
        return <Activity className="w-6 h-6 text-slate-600" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'normal':
        return 'bg-emerald-50 border-emerald-200 text-emerald-900';
      case 'warning':
        return 'bg-amber-50 border-amber-200 text-amber-900';
      case 'critical':
        return 'bg-rose-50 border-rose-200 text-rose-900';
      default:
        return 'bg-slate-50 border-slate-200 text-slate-900';
    }
  };

  const getTemperatureGuidance = (temp) => {
    const t = parseFloat(temp);
    if (isNaN(t)) return null;
    
    if (t < 4.0) {
      return { status: 'normal', message: 'Temperature is within safe range (< 4°C)' };
    } else if (t < 6.0) {
      return { status: 'warning', message: 'Temperature is in warning range (4-6°C) - alarm will be created' };
    } else {
      return { status: 'critical', message: 'Temperature is critical (≥ 6°C) - critical alarm will be created' };
    }
  };

  const guidance = formData.temperature_celsius ? getTemperatureGuidance(formData.temperature_celsius) : null;

  return (
    <div className="space-y-6 sm:space-y-8" data-testid="sensor-test-console">
      {/* Header */}
      <div>
        <div className="flex items-center gap-2 sm:gap-3 mb-2 flex-wrap">
          <Thermometer className="w-7 h-7 sm:w-9 sm:h-9 lg:w-10 lg:h-10 text-blue-600 flex-shrink-0" />
          <h1
            className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-800"
            style={{ fontFamily: 'Manrope' }}
          >
            Cold Chain Monitoring
          </h1>
          <DataSourceBadge source="live" label="Live Sensor Data" />
        </div>
      </div>

      {/* Monitoring Session Manager — SCM only */}
      {perms.canStartMonitoringSession && (
        <div data-testid="monitoring-session-manager">
          <MonitoringSessionManager />
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8">
        {/* Form — SCM only (sensor ingestion is operational write) */}
        {perms.canIngestSensorReading && (
        <div className="bg-white rounded-xl p-4 sm:p-6 lg:p-8 border border-slate-200" data-testid="manual-sensor-entry">
          <h2 className="text-lg sm:text-xl lg:text-2xl font-semibold text-slate-800 mb-2" style={{ fontFamily: 'Manrope' }}>
            Manual Sensor Entry
          </h2>
          <p className="text-xs sm:text-sm text-slate-600 mb-4 sm:mb-6 leading-relaxed">
            Used for validation, demonstrations, testing, and manual fallback scenarios. In production environments, PureSustain automatically receives data from connected sensors, IoT devices, SCADA systems, gateways, and integrations.
          </p>

          <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5">
            {/* Required Fields */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5 sm:mb-2">
                Batch ID <span className="text-rose-500">*</span>
              </label>
              <input
                type="text"
                name="batch_id"
                value={formData.batch_id}
                onChange={handleInputChange}
                placeholder="e.g., batch-001"
                required
                inputMode="text"
                className="w-full h-11 px-4 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5 sm:mb-2">
                Temperature (°C) <span className="text-rose-500">*</span>
              </label>
              <input
                type="number"
                step="0.1"
                name="temperature_celsius"
                value={formData.temperature_celsius}
                onChange={handleInputChange}
                placeholder="e.g., 3.5"
                required
                inputMode="decimal"
                className="w-full h-11 px-4 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base"
              />
              {guidance && (
                <div className={`mt-2 p-2.5 sm:p-3 rounded-lg border text-xs sm:text-sm ${getStatusColor(guidance.status)}`}>
                  <div className="flex items-start gap-2">
                    {getStatusIcon(guidance.status)}
                    <span className="font-medium">{guidance.message}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Optional Fields */}
            <div className="pt-3 sm:pt-4 border-t border-slate-200">
              <p className="text-sm font-medium text-slate-700 mb-3 sm:mb-4">Optional Product Information</p>

              <div className="space-y-3 sm:space-y-4">
                <div>
                  <label className="block text-sm text-slate-600 mb-1.5 sm:mb-2">Product Name</label>
                  <input
                    type="text"
                    name="product_name"
                    value={formData.product_name}
                    onChange={handleInputChange}
                    placeholder="e.g., Premium Atlantic Salmon"
                    className="w-full h-11 px-4 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base"
                  />
                </div>

                <div>
                  <label className="block text-sm text-slate-600 mb-1.5 sm:mb-2">Product Type</label>
                  <input
                    type="text"
                    name="product_type"
                    value={formData.product_type}
                    onChange={handleInputChange}
                    placeholder="e.g., Fresh Seafood"
                    className="w-full h-11 px-4 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base"
                  />
                </div>

                <div>
                  <label className="block text-sm text-slate-600 mb-1.5 sm:mb-2">Species or Category</label>
                  <input
                    type="text"
                    name="species_or_category"
                    value={formData.species_or_category}
                    onChange={handleInputChange}
                    placeholder="e.g., Atlantic Salmon"
                    className="w-full h-11 px-4 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base"
                  />
                </div>

                <div>
                  <label className="block text-sm text-slate-600 mb-1.5 sm:mb-2">Monitoring Session ID</label>
                  <input
                    type="text"
                    name="test_session_id"
                    value={formData.test_session_id}
                    onChange={handleInputChange}
                    placeholder="e.g., SHIPMENT-2026-W22"
                    className="w-full h-11 px-4 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base"
                  />
                </div>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white h-12 text-base font-semibold"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  Submitting...
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  <Send className="w-5 h-5" />
                  Submit Sensor Reading
                </span>
              )}
            </Button>
          </form>
        </div>
        )}

        {/* Response Display */}
        <div className="space-y-4 sm:space-y-6">
          {/* Temperature Thresholds Reference */}
          <div className="bg-white rounded-xl p-4 sm:p-6 lg:p-8 border border-slate-200">
            <h3 className="text-base sm:text-lg lg:text-xl font-semibold text-slate-800 mb-3 sm:mb-4" style={{ fontFamily: 'Manrope' }}>
              Temperature Thresholds
            </h3>
            <div className="space-y-2 sm:space-y-3">
              <div className="flex items-center gap-3 p-2.5 sm:p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                <CheckCircle className="w-5 h-5 text-emerald-600" />
                <div>
                  <p className="font-semibold text-emerald-900">Normal</p>
                  <p className="text-sm text-emerald-700">Temperature {'<'} 4°C</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
                <div>
                  <p className="font-semibold text-amber-900">Warning</p>
                  <p className="text-sm text-amber-700">Temperature 4°C to {'<'} 6°C</p>
                  <p className="text-xs text-amber-600 mt-1">⚠️ Alarm will be created</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-rose-50 border border-rose-200 rounded-lg">
                <XCircle className="w-5 h-5 text-rose-600" />
                <div>
                  <p className="font-semibold text-rose-900">Critical</p>
                  <p className="text-sm text-rose-700">Temperature ≥ 6°C</p>
                  <p className="text-xs text-rose-600 mt-1">🚨 Critical alarm will be created</p>
                </div>
              </div>
            </div>
          </div>

          {/* Response */}
          {response && !response.error && (
            <div className="bg-white rounded-xl p-8 border border-slate-200">
              <div className="flex items-center gap-2 mb-4">
                <CheckCircle className="w-6 h-6 text-emerald-600" />
                <h3 className="text-xl font-semibold text-slate-800" style={{ fontFamily: 'Manrope' }}>
                  Reading Recorded
                </h3>
              </div>

              <div className="space-y-4">
                {/* Status Badge */}
                <div className={`flex items-center gap-3 p-4 rounded-lg border ${getStatusColor(response.status)}`}>
                  {getStatusIcon(response.status)}
                  <div>
                    <p className="text-sm font-medium uppercase tracking-wider">Status</p>
                    <p className="text-2xl font-bold">{response.status}</p>
                  </div>
                </div>

                {/* Details */}
                <div className="space-y-2 p-4 bg-slate-50 rounded-lg border border-slate-200">
                  <div className="flex justify-between">
                    <span className="text-sm text-slate-600">Reading ID:</span>
                    <span className="text-sm font-mono text-slate-900">{response.id}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-slate-600">Batch ID:</span>
                    <span className="text-sm font-semibold text-slate-900">{response.batch_id}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-slate-600">Temperature:</span>
                    <span className="text-sm font-bold text-slate-900">{response.temperature_celsius}°C</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-slate-600">Sensor ID:</span>
                    <span className="text-sm font-mono text-slate-900">{response.sensor_id}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-slate-600">Timestamp:</span>
                    <span className="text-sm text-slate-900">
                      {new Date(response.timestamp).toLocaleString('en-NO')}
                    </span>
                  </div>
                  
                  {response.product_name && (
                    <>
                      <div className="pt-2 mt-2 border-t border-slate-200"></div>
                      <div className="flex justify-between">
                        <span className="text-sm text-slate-600">Product:</span>
                        <span className="text-sm font-semibold text-slate-900">{response.product_name}</span>
                      </div>
                      {response.species_or_category && (
                        <div className="flex justify-between">
                          <span className="text-sm text-slate-600">Species:</span>
                          <span className="text-sm text-slate-900">{response.species_or_category}</span>
                        </div>
                      )}
                      {response.test_session_id && (
                        <div className="flex justify-between">
                          <span className="text-sm text-slate-600">Session:</span>
                          <span className="text-sm font-mono text-slate-900">{response.test_session_id}</span>
                        </div>
                      )}
                    </>
                  )}
                </div>

                {/* Alarm Notice */}
                {(response.status === 'warning' || response.status === 'critical') && (
                  <div className={`p-4 rounded-lg border ${
                    response.status === 'critical' 
                      ? 'bg-rose-50 border-rose-200' 
                      : 'bg-amber-50 border-amber-200'
                  }`}>
                    <div className="flex items-start gap-2">
                      <AlertTriangle className={`w-5 h-5 mt-0.5 ${
                        response.status === 'critical' ? 'text-rose-600' : 'text-amber-600'
                      }`} />
                      <div>
                        <p className={`font-semibold ${
                          response.status === 'critical' ? 'text-rose-900' : 'text-amber-900'
                        }`}>
                          Alarm Created
                        </p>
                        <p className={`text-sm ${
                          response.status === 'critical' ? 'text-rose-700' : 'text-amber-700'
                        }`}>
                          A {response.status} alarm has been automatically created for this reading.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Error Response */}
          {response && response.error && (
            <div className="bg-rose-50 rounded-xl p-8 border border-rose-200">
              <div className="flex items-center gap-2 mb-4">
                <XCircle className="w-6 h-6 text-rose-600" />
                <h3 className="text-xl font-semibold text-rose-900" style={{ fontFamily: 'Manrope' }}>
                  Error
                </h3>
              </div>
              <p className="text-rose-700">{response.error}</p>
            </div>
          )}
        </div>
      </div>

      {/* Quick Tips */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-blue-900 mb-3">Monitoring Guidelines</h3>
        <ul className="space-y-2 text-sm text-blue-800">
          <li className="flex items-start gap-2">
            <span className="text-blue-600">•</span>
            <span><strong>Batch ID</strong> and <strong>Temperature</strong> are required fields</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-blue-600">•</span>
            <span>Add product information to track performance in <strong>Product Insights</strong></span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-blue-600">•</span>
            <span>Use the same <strong>Monitoring Session ID</strong> to group related shipments or production runs</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-blue-600">•</span>
            <span>Temperature below 4°C is normal, 4-6°C triggers warning, 6°C+ triggers critical alarm</span>
          </li>
        </ul>
      </div>
    </div>
  );
};

export default SensorTestConsole;
